import type { ReactNode } from "react";
import { useEffect, useMemo, useRef, useState } from "react";

import dayjs from "dayjs";
import type { SeriesLineOptions } from "highcharts";
import styled from "styled-components";

import { SWReactIcons } from "@similarweb/icons";
import type { SimplifiedSeries, SubtitleFilter } from "@similarweb/pro-logic-components";
import { LEGEND_POSITIONS, LineGraph, serverResponseFormat } from "@similarweb/pro-logic-components";
import { colorsPalettes } from "@similarweb/styles";
import { Tooltip } from "@similarweb/ui-components";

import type { ISwitcherConfig, ISwitcherConfigItem } from "app/components/React/WidgetContainer/types";
import { WidgetContainer } from "app/components/React/WidgetContainer/WidgetContainer";
import { translate } from "app/filters/i18nFilter";
import { abbrNumberFilter } from "app/filters/ngFilters";
import { useDisplayAdvertiserPublisherDataAndParams } from "app/pages/digital-marketing/ad-intelligence/common/hooks/useDisplayAdvertiserPublisherDataAndParams";
// fallow-ignore-next-line boundary-violation
import { GranularitySwitcherEnum } from "app/pages/industry-analysis/category-share/CategoryShareGraph/CategoryShareGraphTypes";
// fallow-ignore-next-line boundary-violation
import { GRANULARITY_SWITCHER_OPTIONS } from "app/common/keyword-research/keywords-overview/trendOverTimeConsts";
import DurationService from "app/services/DurationService";
import { allTrackers } from "app/services/track/track";

import type { GranularityNames } from "app/UtilitiesAndConstants/Constants/Moment";
import { getPNG } from "app/UtilitiesAndConstants/UtilityFunctions/getPNG";
import type { Entries } from "pro-features/types";

import type { BreakdownByGranularity } from "../../advertisers/overview/components/types";
import { EDataType } from "../../advertisers/overview/components/types";
import type { AdIntelligenceController } from "../shared/types/types";
import {
    getErrorTypeFromTableNoDataType,
    getNoDataObject,
    isDailyGranularityLocked,
    NA_VALUE,
    shouldUseAdIntelligencePreviewHook,
} from "../utils/utils";

import { getChartConfig } from "./configs/PerformanceOverTimeGraphConfig";
import { GraphContainer, GraphLegendWrapper } from "./StyledComponents";
import type { GranularityKey, DevicePerformanceData } from "./types";
import {
    getCustomGranularitySwitcherOptions,
    getEmptyGraphPropsWithThirdMetric,
    granularityNameToValue,
} from "./utils/graphUtils";

const LockedIconContainer = styled.div`
    overflow: hidden;
`;

interface IPerformanceOverTimeChartProps {
    performanceData?: DevicePerformanceData;
    hasAdIntelMetrics?: boolean;
    isLoading: boolean;
    isError: boolean;
    subtitleFilters: SubtitleFilter[];
    isPreview?: boolean;
    controller?: AdIntelligenceController;
    defaultGranularity?: GranularitySwitcherEnum;
    hideGranularityFilter?: boolean;
    automationId?: string;
    metricSelectionMode?: "legend" | "segmented";
    /** Notice rendered above the chart; stays visible in the empty state. */
    noticeSlot?: ReactNode;
}

export const PerformanceOverTimeChart = ({
    performanceData,
    hasAdIntelMetrics,
    isLoading,
    isError,
    subtitleFilters,
    isPreview = false,
    controller = "advertiser",
    defaultGranularity = GranularitySwitcherEnum.WEEKLY,
    hideGranularityFilter = false,
    automationId = "performance-overtime-graph",
    metricSelectionMode = "legend",
    noticeSlot,
}: IPerformanceOverTimeChartProps) => {
    const isHooked = shouldUseAdIntelligencePreviewHook(isPreview);
    const isSegmented = metricSelectionMode === "segmented";
    const thirdMetric = controller === "advertiser" ? EDataType.VISITS : EDataType.PAGE_VIEWS;
    const params = useDisplayAdvertiserPublisherDataAndParams();
    const chartRef = useRef(null);

    const [selectedGranularity, setSelectedGranularity] = useState<GranularitySwitcherEnum>(defaultGranularity);
    const [selectedLegends, setSelectedLegends] = useState<EDataType[]>([thirdMetric]);

    const isGraphLoading = isLoading;

    const performanceTotals = performanceData?.total;

    const selectedBreakdownData = useMemo(() => {
        const currentGranularityKey = GRANULARITY_SWITCHER_OPTIONS[
            selectedGranularity
        ].value.toLowerCase() as GranularityKey;
        const breakdownByGranularity = performanceData?.[currentGranularityKey] || {};

        return Object.entries(breakdownByGranularity) as Entries<BreakdownByGranularity>;
    }, [performanceData, selectedGranularity]);

    const graphData: SimplifiedSeries<SeriesLineOptions> = useMemo(() => {
        const initialGraphProps = getEmptyGraphPropsWithThirdMetric(
            performanceTotals?.spend,
            performanceTotals?.impressions,
            performanceTotals?.[thirdMetric],
            thirdMetric,
            isPreview,
        );

        return selectedBreakdownData.reduce((graphAccumulator, [dateString, performanceItem]) => {
            const timestampValue = dayjs.utc(dateString, serverResponseFormat).valueOf();
            const thirdMetricValue = performanceItem[thirdMetric]?.value || 0;
            const spend = performanceItem.spend?.value || 0;
            const impressions = performanceItem.impressions?.value || 0;

            const orderedValues = isPreview
                ? [thirdMetricValue, spend, impressions]
                : [spend, impressions, thirdMetricValue];

            orderedValues.forEach((value, index) => {
                graphAccumulator[index].data.push([timestampValue, value]);
            });

            return graphAccumulator;
        }, initialGraphProps);
    }, [performanceTotals, selectedBreakdownData, isPreview, thirdMetric]);

    const graphDataWithVisibility = useMemo(() => {
        return graphData.map((item) => {
            const yAxis = selectedLegends.findIndex((legend) => legend === (item.id as EDataType));
            const isSpendOrImpressions =
                (item.id as EDataType) === EDataType.SPEND || (item.id as EDataType) === EDataType.IMPRESSIONS;
            const shouldDisable = (hasAdIntelMetrics === false || isHooked) && isSpendOrImpressions;
            return {
                ...item,
                name: translate(item.name),
                visible: selectedLegends.includes(item.id as EDataType),
                yAxis: yAxis === -1 ? 0 : yAxis,
                ...(shouldDisable ? { isDisabled: true, total: 0 } : {}),
            };
        });
    }, [graphData, selectedLegends, hasAdIntelMetrics, isHooked]);

    const hideDailyGranularity = isDailyGranularityLocked();
    const customGranularitySwitcherOptions = useMemo(() => {
        return getCustomGranularitySwitcherOptions(
            params.webSource,
            hasAdIntelMetrics,
            isGraphLoading,
            DurationService.getDurationData(params.duration).forAPI.isWindow,
            hideDailyGranularity,
        );
    }, [params.webSource, hasAdIntelMetrics, isGraphLoading, params.duration, hideDailyGranularity]);

    useEffect(() => {
        if (isGraphLoading) {
            return;
        }
        const currentGranularity = customGranularitySwitcherOptions.find(
            (option) => option.value === GRANULARITY_SWITCHER_OPTIONS[selectedGranularity].value,
        );
        if (!currentGranularity || currentGranularity.disabled) {
            const availableOption = [...customGranularitySwitcherOptions].reverse().find((option) => !option.disabled);

            if (availableOption) {
                setSelectedGranularity(granularityNameToValue[availableOption.value as GranularityNames]);
            }
        }
    }, [customGranularitySwitcherOptions, isGraphLoading, selectedGranularity]);

    useEffect(() => {
        if (hasAdIntelMetrics === undefined) {
            return;
        }
        const canUseAdIntelMetrics = hasAdIntelMetrics && !isHooked;
        if (isSegmented) {
            setSelectedLegends([canUseAdIntelMetrics ? EDataType.SPEND : thirdMetric]);
        } else {
            setSelectedLegends(canUseAdIntelMetrics ? [EDataType.SPEND, EDataType.IMPRESSIONS] : [thirdMetric]);
        }
    }, [hasAdIntelMetrics, isHooked, thirdMetric, isSegmented]);

    const legendValueFilter = (value: number, legendData: SimplifiedSeries<SeriesLineOptions>[number]) => {
        const isSpendOrImpressions =
            (legendData.id as EDataType) === EDataType.SPEND || (legendData.id as EDataType) === EDataType.IMPRESSIONS;
        if ((isHooked || hasAdIntelMetrics === false) && isSpendOrImpressions) {
            return (
                <Tooltip content={translate("ad.intelligence.overview.performance.over.time.widget.locked.tooltip")}>
                    <LockedIconContainer>
                        <SWReactIcons
                            iconName="locked"
                            size="xs"
                            iconColor={colorsPalettes.carbon[500]}
                        />
                    </LockedIconContainer>
                </Tooltip>
            );
        }

        if (!value) {
            return NA_VALUE;
        }

        const valueAbbr = abbrNumberFilter()(value);
        if (legendData.id === EDataType.SPEND) {
            return `$${valueAbbr}`;
        }
        return valueAbbr;
    };

    const handleGranularitySwitchChange = (filteredIndex: number) => {
        const item = customGranularitySwitcherOptions[filteredIndex];
        if (!item) {
            return;
        }
        const value = granularityNameToValue[item.value as GranularityNames];
        setSelectedGranularity(value);
        allTrackers.dispatchTrackEvent(
            "Time Frame Button",
            "switch",
            `Over Time Graph/Display Advertising Performance/${item.value}`,
        );
    };

    const handleLegendSelectionTracking = (name: string, action: string) => {
        allTrackers.dispatchTrackEvent("Graph Filter", "click", `Over Time Graph/${name}/${action}`);
    };

    const handleLegendSelectionChange = (newlySelectedLegends) => {
        const legendIds = newlySelectedLegends.map((legend) => legend.id as EDataType);
        if (legendIds.length === 0) {
            return;
        }
        setSelectedLegends(legendIds);
    };

    const granularitySelectedIndex = customGranularitySwitcherOptions.findIndex(
        (option) => option.value === GRANULARITY_SWITCHER_OPTIONS[selectedGranularity].value,
    );

    const switcherGranularity: ISwitcherConfig = {
        items: customGranularitySwitcherOptions,
        onItemClick: handleGranularitySwitchChange,
        initialSelectedItem: granularitySelectedIndex,
    };

    const isMetricLocked = isGraphLoading || hasAdIntelMetrics !== true || isHooked;

    const metricSwitcherItems: ISwitcherConfigItem[] = [
        {
            title: "ad.intelligence.overview.performance.over.time.widget.spend",
            value: EDataType.SPEND,
            disabled: isMetricLocked,
            ...(isMetricLocked && !isGraphLoading
                ? {
                      tooltipText: "ad.intelligence.overview.performance.over.time.widget.locked.tooltip",
                      isLocked: true,
                  }
                : {}),
            dataAutomation: "performance-over-time-metric-spend",
        },
        {
            title: "ad.intelligence.overview.performance.over.time.widget.impressions",
            value: EDataType.IMPRESSIONS,
            disabled: isMetricLocked,
            ...(isMetricLocked && !isGraphLoading
                ? {
                      tooltipText: "ad.intelligence.overview.performance.over.time.widget.locked.tooltip",
                      isLocked: true,
                  }
                : {}),
            dataAutomation: "performance-over-time-metric-impressions",
        },
        {
            title: `ad.intelligence.overview.performance.over.time.widget.${thirdMetric}`,
            value: thirdMetric,
            disabled: isGraphLoading,
            dataAutomation: `performance-over-time-metric-${thirdMetric}`,
        },
    ];

    const handleMetricSwitchChange = (filteredIndex: number) => {
        const item = metricSwitcherItems[filteredIndex];
        if (!item || item.disabled) {
            return;
        }
        setSelectedLegends([item.value as EDataType]);
        allTrackers.dispatchTrackEvent("Graph Filter", "click", `Over Time Graph/${item.value}/switch`);
    };

    const metricSwitcher: ISwitcherConfig = {
        items: metricSwitcherItems,
        initialSelectedItem: Math.max(
            0,
            metricSwitcherItems.findIndex((item) => item.value === selectedLegends[0]),
        ),
        onItemClick: handleMetricSwitchChange,
    };

    const chartConfig = useMemo(
        () =>
            getChartConfig(
                GRANULARITY_SWITCHER_OPTIONS[selectedGranularity].value as GranularityNames,
                DurationService.getDurationData(params.duration).raw.to,
                selectedLegends as any,
                !performanceTotals?.impressions && !performanceTotals?.spend && !performanceTotals?.[thirdMetric],
                selectedBreakdownData,
                isPreview,
                thirdMetric,
                isSegmented,
            ),
        [
            selectedGranularity,
            params.duration,
            selectedLegends,
            performanceTotals,
            selectedBreakdownData,
            isPreview,
            thirdMetric,
            isSegmented,
        ],
    );

    const hasNoData = selectedBreakdownData.every(
        ([, item]) => !item.spend?.value && !item.impressions?.value && !item[thirdMetric]?.value,
    );
    const { title, subtitle, type } = getNoDataObject(isError, controller);

    return (
        <div
            ref={chartRef}
            data-automation={automationId}
        >
            <WidgetContainer
                // key is used to force re-render the graph when the legends change
                // this is a workaround to fix the issue where the graph is not updated when the legends change by useEffect
                key={selectedLegends.join(",")}
                withBorder
                wrapperHeight="auto"
                title={translate("ad.intelligence.overview.performance.over.time.widget.title")}
                tooltip={translate("ad.intelligence.overview.performance.over.time.widget.tooltip")}
                subtitleFilters={subtitleFilters}
                showSubtitleFiltersLoadingState={false}
                isLoading={isGraphLoading}
                loaderWidth={"100%"}
                loaderHeight={208}
                noticeSlot={noticeSlot}
                noData={!isGraphLoading ? hasNoData : null}
                noDataTitle={title}
                noDataSubtitle={subtitle}
                noDataType={getErrorTypeFromTableNoDataType(type)}
                dynamicSwitcherConfig={[
                    ...(isSegmented ? [metricSwitcher] : []),
                    ...(hideGranularityFilter ? [] : [switcherGranularity]),
                ]}
                onDownloadClick={() =>
                    chartRef.current &&
                    getPNG(
                        chartRef,
                        0,
                        50,
                        translate("ad.intelligence.overview.performance.over.time.widget.title"),
                    )?.()
                }
            >
                <GraphContainer>
                    <LineGraph
                        shouldPresentLegend={!isSegmented}
                        shouldPresentValueAtLegend
                        isSelectableCheckboxes
                        legendPosition={LEGEND_POSITIONS.TOP}
                        withMarkers={selectedBreakdownData.length === 1}
                        // @ts-expect-error FIXME: Type '(value: number, legendData: SimplifiedSeries<SeriesLineOptions>[number]) => string | JSX.Element' is not assignable to type '(value: number, data: Data | undefined) => string'
                        legendValueFilter={legendValueFilter}
                        customLegendComponentWrapper={GraphLegendWrapper}
                        maxLegendItemsSelected={2}
                        data={graphDataWithVisibility}
                        graphConfig={chartConfig}
                        disabledLegendItemTooltip={
                            hasAdIntelMetrics && !isHooked
                                ? translate("ad.intelligence.overview.graph.widget.disabled.max.items.legend.tooltip")
                                : undefined
                        }
                        legendToggleHandlerCallback={handleLegendSelectionChange}
                        customLegendTrackingHandler={handleLegendSelectionTracking}
                    />
                </GraphContainer>
            </WidgetContainer>
        </div>
    );
};
